import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, Button, Space, Tag, Typography, Row, Col, Avatar } from 'antd';
import { EditOutlined, PlusOutlined, AppstoreOutlined } from '@ant-design/icons';
import http from '../../http';
import ApplicationAddPage from './ApplicationAddPage';

const formatBytes = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const OS_COLORS = { windows: 'blue', linux: 'green', macos: 'purple', other: 'default' };

const ApplicationListPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [edit, setEdit] = useState(false);
    const [item, setItem] = useState(null);

    const fetchData = () => {
        setLoading(true);
        http.get('/api/terminal/applications/').then(resp => {
            setData(resp.data);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (record, isEdit) => {
        setEdit(!!isEdit);
        setItem(isEdit ? record : null);
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setEdit(false);
        setItem(null);
    };

    const handleSuccess = () => {
        handleClose();
        fetchData();
    };

    const columns = [
        {
            title: 'Logo',
            dataIndex: 'logo',
            key: 'logo',
            width: '6%',
            render: (logo) => (
                <Avatar
                    shape="square"
                    src={logo || undefined}
                    icon={<AppstoreOutlined />}
                />
            ),
        },
        {
            title: 'Application',
            dataIndex: 'name',
            key: 'name',
            width: '13%',
        },
        {
            title: 'Bundle ID',
            dataIndex: 'bundle_id',
            key: 'bundle_id',
            width: '14%',
            ellipsis: true,
            render: (v) => v || '-',
        },
        {
            title: 'Filename',
            key: 'filename',
            width: '20%',
            ellipsis: true,
            render: (_, record) => {
                const ext = record.installer?.split('?')[0].split('.').pop();
                return `${record.name}${record.version ? `_${record.version}` : ''}.${ext}`;
            },
        },
        {
            title: 'OS',
            dataIndex: 'os',
            key: 'os',
            width: '10%',
            render: (os) => os ? <Tag color={OS_COLORS[os] || 'default'}>{os}</Tag> : '-',
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            width: '10%',
            render: (v) => v || '-',
        },
        {
            title: 'Size',
            dataIndex: 'installer_size',
            key: 'installer_size',
            width: '10%',
            render: (size) => formatBytes(size),
        },
        {
            title: 'Updated At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: '15%',
            render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: 'Color',
            dataIndex: 'color_code',
            key: 'color_code',
            width: '10%',
            render: (color) => color ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: color, border: '1px solid #d9d9d9' }} />
                    {color}
                </div>
            ) : '-',
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: '8%',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Typography.Link onClick={() => handleOpenModal(record, true)}>
                        <EditOutlined />
                    </Typography.Link>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <span style={{ fontWeight: 'bold', fontSize: 30 }}>Applications</span>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<PlusOutlined />}
                        style={{ background: '#377EF3' }}
                        onClick={() => handleOpenModal()}
                    />
                </Col>
            </Row>
            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ y: 'calc(100vh - 250px)' }}
            />
            <ApplicationAddPage
                visible={showModal}
                onClose={handleClose}
                onSuccessSubmit={handleSuccess}
                edit={edit}
                editItem={item}
            />
        </>
    );
};

export default ApplicationListPage;
